import {FC, ComponentProps, useMemo, useCallback, useContext, useState} from 'react';
import {forEach, transform, get, keys, sortBy, isPlainObject, has, castArray, isString} from 'lodash';
import {
  FetchData, FetchDataError, Loader, ResourceModal,
  generatePropertyFromSchema, interpolateRoute, request
} from 'apstra-ui-common';

import IBAContext from '../IBAContext';
import PredefinedEntityForm from './PredefinedEntityForm';
import {addError, searchForGenericErrors} from '../../processErrors';

type PredefinedEntityModalProps = {
  mode?: 'create' | 'update' | 'clone';
  baseRoute: string;
  generateResourceHref: (params: {blueprintId: string; entityId: string}) => string;
  entities: any[];
  entityName?: string;
  entityId?: string;
  fetchEntityParameters?: (params: any) => Promise<any>;
  resourceName?: string;
  helpPageId?: string;
} & Pick<ComponentProps<typeof PredefinedEntityForm>, 'renderers' | 'columnWidths'>
  & Partial<Pick<ComponentProps<typeof ResourceModal>, 'trigger' | 'open' | 'onClose' | 'extraActions'>>;

const PredefinedEntityModal: FC<PredefinedEntityModalProps> = ({
  mode = 'create', entities, entityId, entityName, fetchEntityParameters,
  baseRoute, resourceName, generateResourceHref,
  renderers, columnWidths,
  trigger, open, onClose, helpPageId, extraActions,
}) => {
  const {routes, blueprintId, blueprintPermissions} = useContext(IBAContext);

  const [parameters, setParameters] = useState({});
  const setParameter = useCallback((name, value) => {
    setParameters({...parameters, [name]: value});
  }, [parameters, setParameters]);

  const [currentEntityName, setCurrentEntityName] = useState(entityName);
  const entitiesByName = useMemo(() => transform(
    sortBy(entities, ({name, schema}) => get(schema, ['properties', 'label', 'default'], name)),
    (result, entity) => {
      result[entity.name] = entity;
    },
    {}
  ), [entities]);
  const setEntityName = useCallback((entityName) => {
    setCurrentEntityName(entityName);
    const parameters = {};
    const entity = entityName ? entitiesByName[entityName] : null;
    if (entity) {
      forEach(get(entity, ['schema', 'properties'], {}), (schema, name) => {
        parameters[name] = generatePropertyFromSchema(schema);
      });
    }
    setParameters(parameters);
  }, [entitiesByName]);

  const resetState = useCallback(() => {
    setEntityName(entityName ?? keys(entitiesByName)[0]);
  }, [entitiesByName, entityName, setEntityName]);

  const currentEntity = currentEntityName ? entitiesByName[currentEntityName] : undefined;

  const fetchData = async (fetchParams) => {
    if (fetchEntityParameters) {
      const parameters = await fetchEntityParameters(fetchParams);
      setParameters(parameters);
    }
  };

  const processErrors = useCallback(({errors: errorsProps}) => {
    const result: any[] = [];
    const properties = get(currentEntity, ['schema', 'properties']);
    const errors = isString(errorsProps) ? castArray(errorsProps) : errorsProps;
    forEach(errors, (propertyErrors, propertyName) => {
      if (!has(properties, propertyName)) {
        searchForGenericErrors(result, propertyErrors, propertyName);
      } else if (isPlainObject(propertyErrors)) {
        result.push({type: 'property', propertyName, propertyErrors});
      } else {
        addError(result, propertyErrors, (message) => ({type: 'property', propertyName, message}));
      }
    });
    return result;
  }, [currentEntity]);

  const submit = useCallback(async () => {
    const route = interpolateRoute(baseRoute, {blueprintId}) +
      '/' + currentEntityName + (mode === 'update' ? '/' + entityId : '');
    const method = mode === 'update' ? 'PUT' : 'POST';
    const {id: newEntityId} = await request(route, {method, body: JSON.stringify(parameters)});
    return {
      resourceLabel: (parameters as any).label,
      resourceHref: generateResourceHref({blueprintId, entityId: mode === 'update' ? entityId : newEntityId})
    };
  }, [mode, parameters, baseRoute, blueprintId, entityId, currentEntityName, generateResourceHref]);

  const onSuccess = useCallback(({result: {resourceHref}, createAnother}) => {
    if (!createAnother) location.href = `/#${resourceHref}`;
  }, []);

  return (
    <FetchData
      fetchData={fetchData}
      fetchParams={{routes, blueprintPermissions, blueprintId, entityName: currentEntityName, entityId}}
      pollingInterval={null}
      customLoader
    >
      {({loaderVisible, fetchDataError}) =>
        <ResourceModal
          helpPageId={helpPageId}
          mode={mode}
          resourceName={resourceName}
          titlesByMode={{
            create: 'Instantiate',
            update: 'Edit',
            clone: 'Clone',
          }}
          open={open}
          trigger={trigger}
          onClose={onClose}
          resetState={resetState}
          submit={submit}
          submitAvailable={!loaderVisible && !fetchDataError}
          processErrors={processErrors}
          onSuccess={onSuccess}
          extraActions={extraActions}
        >
          {loaderVisible ?
            <Loader />
          : fetchDataError ?
            <FetchDataError error={fetchDataError} />
          :
            <PredefinedEntityForm
              label={resourceName}
              parameters={parameters}
              entityChoiceDisabled={mode !== 'create'}
              entity={currentEntity}
              entityName={currentEntityName}
              entitiesByName={entitiesByName}
              setEntityName={setEntityName}
              setParameter={setParameter}
              renderers={renderers}
              columnWidths={columnWidths}
            />
          }
        </ResourceModal>
      }
    </FetchData>
  );
};

export default PredefinedEntityModal;
